/**
 * @file: useHandleServiceSubjectPress.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { LinkButton } from '@euler/app/components/LinkButton';
import { useMemberPickerBottomSheetContext } from '@euler/app/components/MemberPickerBottomSheet';
import { WiredRememberLastAssignButton } from '@euler/app/components/settings/RememberLastAssignmentButton';
import { useServiceSubjectsContext } from '@euler/app/flows/order/pages/ServiceSubjectsScreen/components/ServiceSubjectsContext';
import { findServiceSubjectIndex } from '@euler/app/flows/order/pages/ServiceSubjectsScreen/functions/findServiceSubjectIndex';
import { serviceSubjectItemToSubject } from '@euler/app/flows/order/pages/ServiceSubjectsScreen/functions/serviceSubjectItemToSubject';
import { useServiceSubjectsHost } from '@euler/app/flows/order/pages/ServiceSubjectsScreen/host';
import { ServiceSubjectItem } from '@euler/app/flows/order/pages/ServiceSubjectsScreen/types';
import { useUserLocalSettings } from '@euler/app/flows/settings/functions/useUserLocalSettings';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { OrgMember } from '@euler/model/entity';
import { OrgUserRoleType, ServiceSubjectType } from '@euler/model/enum';
import { ServiceSubject } from '@euler/model/OrderDetail';
import { showAlert } from '@euler/utils';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { useRef } from 'react';

export function useHandleServiceSubjectPress({
  memberPickerTitle,
  isSubjectReadonly,
}: {
  memberPickerTitle?: string;
  isSubjectReadonly: (item: ServiceSubjectItem) => boolean;
}) {
  const { rpx } = useResponsiveContext();

  const { orderDetail$, skipJobAssignment$, jobAssignmentEnabled } =
    useServiceSubjectsHost();

  const [orderDetail] = useBehaviorSubject(orderDetail$);

  const [skipJobAssignment, setSkipJobAssignment] =
    useBehaviorSubject(skipJobAssignment$);

  const { removeServiceSubject, addServiceSubject } =
    useServiceSubjectsContext();

  const { memberPickerPresentationRequest$ } =
    useMemberPickerBottomSheetContext();

  const lastAssignedMember = useRef<OrgMember>();
  const [{ rememberLastServiceSubjectAssignment }] = useUserLocalSettings();

  return usePersistFn((item: ServiceSubjectItem) => {
    if (isSubjectReadonly(item)) {
      const name =
        item.type === ServiceSubjectType.Inspection ? '检测任务' : '施工项目';
      const msg: string[] = [
        name,
        `"${item.title}"`,
        `已经开始或已完成, 若要取消该${name}, 请前往${name}页面完成相关操作!`,
      ];
      void showAlert('提示', msg.join(''));
      return;
    }

    const addSubjectWithAssign = (member: OrgMember) => {
      lastAssignedMember.current = member;
      const subject = serviceSubjectItemToSubject(item, {
        id: member.id,
        name: member.name,
      });
      addServiceSubject(subject);
    };

    const index = findServiceSubjectIndex(orderDetail, item);
    if (index >= 0) {
      removeServiceSubject(index);
      return;
    }

    if (jobAssignmentEnabled === false) {
      // This context assigns no technicians (e.g. reservations — assigned at
      // order creation), so add every item without an assignee. Inspection is
      // built inline (cast) since serviceSubjectItemToSubject requires one.
      const subject: ServiceSubject =
        item.type === ServiceSubjectType.Inspection
          ? ({
              type: ServiceSubjectType.Inspection,
              name: item.title,
              templateId: item.template.cid,
            } as ServiceSubject)
          : serviceSubjectItemToSubject(item);
      addServiceSubject(subject);
      return;
    }

    if (item.type === ServiceSubjectType.Inspection) {
      // always ask for assignee for inspection service subject
      memberPickerPresentationRequest$.next({
        title: '检测项目派工',
        role: OrgUserRoleType.Technicians,
        onSelect: member => {
          memberPickerPresentationRequest$.next(undefined);
          addSubjectWithAssign(member);
        },
      });
      return;
    }

    if (skipJobAssignment) {
      const subject = serviceSubjectItemToSubject(
        item,
        // honor the last assigned member
        lastAssignedMember.current
          ? {
              id: lastAssignedMember.current.id,
              name: lastAssignedMember.current.name,
            }
          : undefined,
      );
      addServiceSubject(subject);
      return;
    }

    if (lastAssignedMember.current && rememberLastServiceSubjectAssignment) {
      // assign to the last assigned member
      addSubjectWithAssign(lastAssignedMember.current);
      return;
    }

    memberPickerPresentationRequest$.next({
      title: memberPickerTitle ?? '故障诊断或施工项目派工',
      subTitle: (
        <HStack justifyContent="center">
          <WiredRememberLastAssignButton />
          <LinkButton
            onPress={() => {
              setSkipJobAssignment(true);
              memberPickerPresentationRequest$.next(undefined);
              const subject = serviceSubjectItemToSubject(item);
              addServiceSubject(subject);
            }}
            text="本次不再弹出"
            textStyle={{
              fontSize: rpx(12),
              color: '#777',
              marginLeft: rpx(15),
            }}
          />
        </HStack>
      ),
      role: OrgUserRoleType.Technicians,
      onSelect: member => {
        memberPickerPresentationRequest$.next(undefined);
        addSubjectWithAssign(member);
      },
      onDismiss() {
        const subject = serviceSubjectItemToSubject(item);
        addServiceSubject(subject);
      },
    });
  });
}
